import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Progress } from '@/components/ui/progress';
import { Upload, FileIcon, X } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';

interface FileUploaderProps {
  bucketName: string;
  allowedTypes: string[];
  maxSizeMB?: number;
  onUploadComplete: (url: string, fileName: string) => void;
  currentUrl?: string;
  label: string;
  placeholder?: string;
}

export default function FileUploader({
  bucketName,
  allowedTypes,
  maxSizeMB = 50,
  onUploadComplete,
  currentUrl,
  label,
  placeholder
}: FileUploaderProps) {
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [dragOver, setDragOver] = useState(false);

  const uploadFile = async (file: File) => {
    if (!file) return;

    // Validate file type by extension
    const fileExt = file.name.split('.').pop()?.toLowerCase();
    if (!fileExt || !allowedTypes.includes(fileExt)) {
      toast.error(`Invalid file type. Allowed: ${allowedTypes.join(', ')}`);
      return;
    }

    // Validate file size
    if (file.size > maxSizeMB * 1024 * 1024) {
      toast.error(`File too large. Max size: ${maxSizeMB}MB`);
      return;
    }

    setUploading(true);
    setUploadProgress(0);

    try {
      // Create unique filename
      const fileExt = file.name.split('.').pop();
      const fileName = `${Date.now()}-${Math.random().toString(36).substring(2)}.${fileExt}`;
      const filePath = fileName;

      // Upload file
      const { data, error } = await supabase.storage
        .from(bucketName)
        .upload(filePath, file, {
          cacheControl: '3600',
          upsert: false
        });

      if (error) throw error;

      // Get public URL
      const { data: { publicUrl } } = supabase.storage
        .from(bucketName)
        .getPublicUrl(filePath);

      setUploadProgress(100);
      toast.success('File uploaded successfully!');
      onUploadComplete(publicUrl, file.name);
    } catch (error) {
      console.error('Upload error:', error);
      toast.error('Failed to upload file');
    } finally {
      setUploading(false);
      setUploadProgress(0);
    }
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      uploadFile(file);
    }
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(false);
    const file = e.dataTransfer.files[0];
    if (file) {
      uploadFile(file);
    }
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(true);
  };

  const handleDragLeave = () => {
    setDragOver(false);
  };

  const clearFile = () => {
    onUploadComplete('', '');
  };

  return (
    <div className="space-y-2">
      <Label>{label}</Label>
      
      {currentUrl ? (
        <div className="flex items-center gap-2 p-3 border rounded-lg bg-muted/50">
          <FileIcon className="h-4 w-4 text-primary" />
          <span className="text-sm text-muted-foreground flex-1 truncate">
            {currentUrl.split('/').pop()}
          </span>
          <Button
            size="sm"
            variant="ghost"
            onClick={clearFile}
            className="h-6 w-6 p-0"
          >
            <X className="h-3 w-3" />
          </Button>
        </div>
      ) : (
        <div
          className={`border-2 border-dashed rounded-lg p-6 text-center transition-colors ${
            dragOver ? 'border-primary bg-primary/5' : 'border-muted-foreground/25'
          } ${uploading ? 'pointer-events-none opacity-50' : 'cursor-pointer hover:border-primary'}`}
          onDrop={handleDrop}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onClick={() => document.getElementById(`file-${bucketName}`)?.click()}
        >
          <Upload className="h-8 w-8 text-muted-foreground mx-auto mb-2" />
          <p className="text-sm text-muted-foreground mb-1">
            {uploading ? 'Uploading...' : 'Drop files here or click to upload'}
          </p>
          <p className="text-xs text-muted-foreground">
            Max {maxSizeMB}MB • {allowedTypes.join(', ')}
          </p>
          
          {uploading && (
            <div className="mt-4 space-y-2">
              <Progress value={uploadProgress} className="h-1" />
              <p className="text-xs text-primary">{uploadProgress}% uploaded</p>
            </div>
          )}
        </div>
      )}
      
      <Input
        id={`file-${bucketName}`}
        type="file"
        accept={allowedTypes.map(type => `.${type}`).join(',')}
        onChange={handleFileSelect}
        className="hidden"
        disabled={uploading}
      />
      
      {!currentUrl && placeholder && (
        <div className="text-center text-xs text-muted-foreground">
          OR
        </div>
      )}
      
      <Input
        placeholder={placeholder || "Or paste URL here..."}
        value={currentUrl || ''}
        onChange={(e) => onUploadComplete(e.target.value, 'External URL')}
        disabled={uploading}
      />
    </div>
  );
}